import type { InvestmentTier, LoanStructure, MarketPhase } from '../types/gameTypes';
import {
  BALLOON_RISK_PREMIUM,
  INVESTMENT_EXIT_PENALTY,
  INVESTMENT_TIER_META,
  LOAN_BASE_RATE,
  PREPAYMENT_FEE,
} from '../types/gameTypes';

/**
 * Corporate-finance maths for the Financial Desk. Everything in here is pure so the
 * modal's live calculator, the store's round processing and the verification scripts
 * all share exactly one implementation.
 */

/** Base interest per round, driven by the macroeconomic cycle. */
export const baseInterestRateFor = (phase: MarketPhase): number => LOAN_BASE_RATE[phase] ?? LOAN_BASE_RATE.STAGNATION;

/** Rate actually charged on a balloon / zero-coupon loan (base + risk premium). */
export const balloonRateFor = (phase: MarketPhase): number => baseInterestRateFor(phase) + BALLOON_RISK_PREMIUM;

export interface LoanQuote {
  principal: number;
  ratePerRound: number;
  termRounds: number;
  structure: LoanStructure;
  /** Cash leaving the treasury every round (0 for balloon loans). */
  perRoundPayment: number;
  /** Lump sum owed at maturity (principal for interest-only, compounded total for balloon). */
  balloonDue: number;
  /** Total interest / overpayment above the principal. */
  totalInterest: number;
  /** Principal + interest across the whole term. */
  totalRepayment: number;
  maturityRound: number;
}

/**
 * Quote a structured loan.
 * - ANNUITY: fixed annuity payment, principal amortises every round.
 * - INTEREST_ONLY: interest each round, 100 % of principal at maturity.
 * - BALLOON: $0 per round, principal compounded at (rate + 2 % premium) at maturity.
 */
export const quoteLoan = (
  principal: number,
  ratePerRound: number,
  termRounds: number,
  structure: LoanStructure,
  currentRound: number,
): LoanQuote => {
  const safePrincipal = Math.max(0, Math.round(principal));
  const safeTerm = Math.max(1, Math.floor(termRounds));
  const rate = Math.max(0, ratePerRound);
  let perRoundPayment = 0;
  let balloonDue = 0;
  let totalRepayment = 0;

  if (structure === 'ANNUITY') {
    // Standard annuity factor: P · r / (1 − (1+r)^−n); a zero rate degenerates to P / n.
    perRoundPayment = rate > 0 ? (safePrincipal * rate) / (1 - Math.pow(1 + rate, -safeTerm)) : safePrincipal / safeTerm;
    totalRepayment = perRoundPayment * safeTerm;
  } else if (structure === 'INTEREST_ONLY') {
    perRoundPayment = safePrincipal * rate;
    balloonDue = safePrincipal;
    totalRepayment = perRoundPayment * safeTerm + safePrincipal;
  } else {
    perRoundPayment = 0;
    balloonDue = safePrincipal * Math.pow(1 + rate, safeTerm);
    totalRepayment = balloonDue;
  }

  const roundedPayment = Math.round(perRoundPayment);
  const roundedBalloon = Math.round(balloonDue);
  const roundedTotal = structure === 'ANNUITY' ? roundedPayment * safeTerm : Math.round(totalRepayment);
  return {
    principal: safePrincipal,
    ratePerRound: rate,
    termRounds: safeTerm,
    structure,
    perRoundPayment: roundedPayment,
    balloonDue: roundedBalloon,
    totalInterest: Math.max(0, Math.round(roundedTotal - safePrincipal)),
    totalRepayment: roundedTotal,
    maturityRound: currentRound + safeTerm,
  };
};

/** Payoff needed to clear a loan early: outstanding principal + accrued interest + flat fee. */
export const earlyPayoffOf = (outstanding: number, accruedInterest = 0): { fee: number; total: number } => {
  const base = Math.max(0, outstanding) + Math.max(0, accruedInterest);
  const fee = Math.round(base * PREPAYMENT_FEE);
  return { fee, total: Math.round(base) + fee };
};

/** Rounds a fund has been held; used for the horizon / exit-penalty rule. */
export const investmentHorizonRounds = (tier: InvestmentTier): number => INVESTMENT_TIER_META[tier]?.horizonRounds ?? 0;

/** Penalty rate charged on an early withdrawal (0 once the horizon has passed). */
export const investmentExitPenalty = (tier: InvestmentTier, heldRounds: number, earlyPenalty = INVESTMENT_EXIT_PENALTY): number =>
  heldRounds >= investmentHorizonRounds(tier) ? 0 : Math.max(0, earlyPenalty);

/**
 * Live yield for one round of a fund, drawn from the macro cycle:
 * - SAVINGS: fixed 3 % — no volatility at all.
 * - REIT: +1 % … +10 % depending on the cycle (avg ≈ 5 %).
 * - VENTURE: −10 % in a crisis up to +22 % in a boom (avg ≈ 10 %).
 *  Tuned down from the earlier runaway compounding: a 30-round horizon still
 *  grows meaningfully, but no longer turns $5k into $100k.
 */
export const rollInvestmentYield = (tier: InvestmentTier, phase: MarketPhase): number => {
  if (tier === 'SAVINGS') return 0.03;
  if (tier === 'REIT') {
    if (phase === 'BOOM') return 0.07 + Math.random() * 0.03;
    if (phase === 'CRISIS') return 0.01 + Math.random() * 0.01;
    return 0.04 + Math.random() * 0.02;
  }
  if (phase === 'BOOM') return 0.12 + Math.random() * 0.1;
  if (phase === 'CRISIS') return -0.1 + Math.random() * 0.04;
  return 0.04 + Math.random() * 0.08;
};